import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Truck, Pickaxe, Hammer, Construction, LayoutGrid } from "lucide-react";
import SEO from "../components/SEO";
import SectionHeader from "../components/SectionHeader";
import { useSiteData, useLocaleText } from "../contexts/SiteDataContext";

const iconMap = { excavation: Pickaxe, "demolition-work": Hammer, "interlock-kerbstone": LayoutGrid };

const Services = () => {
  const { t } = useTranslation();
  const { services, siteContent } = useSiteData();
  const { pick } = useLocaleText();
  const rental = (services || []).filter((s) => s.type === "rental");
  const other = (services || []).filter((s) => s.type === "other");
  const featuredImage = siteContent?.services_featured_image || "/excavator.jpeg";

  return (
    <div data-testid="page-services">
      <SEO title={t("nav.services")} description={t("services.intro")} path="/services" />

      <section className="relative bg-[#111111] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src="/hero-services.jpg" alt="RTC fleet" className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#111111]/95 via-[#111111]/55 to-transparent rtl:bg-gradient-to-l" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-8 md:py-12">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-12 h-[2px] bg-[#FF5A00]" />
            <span className="font-mono-rtc text-[11px] uppercase tracking-[0.3em] text-[#FF5A00]">{t("services.eyebrow")}</span>
          </div>
          <h1 className="font-display uppercase font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight">
            {t("services.title1")} <br />
            <span className="text-[#FF5A00]">{t("services.title2")}</span> <br /> {t("services.title3")}
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-white/80">{t("services.intro")}</p>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 order-2 lg:order-1">
              <SectionHeader eyebrow={t("services.division1Label")} title={t("services.division1Title")} subtitle={t("services.division1Subtitle")} />
              <div className="mt-8 mb-4 flex items-center gap-3">
                <span className="font-display uppercase font-bold text-lg md:text-xl text-[#111111]">Rental Services</span>
                <span className="w-8 h-[2px] bg-[#FF5A00]" />
              </div>
              <ul className="grid grid-cols-2 gap-3">
                {rental.map((e) => (
                  <li key={e.slug} className="flex items-start gap-2 text-[#111111]">
                    <span className="w-1.5 h-1.5 bg-[#FF5A00] mt-2 shrink-0" />
                    <span className="font-medium">{pick(e.name)}</span>
                  </li>
                ))}
              </ul>
              <Link to="/services/rental" className="mt-6 inline-flex items-center gap-2 bg-[#FF5A00] text-white px-7 py-3.5 font-bold uppercase tracking-wide hover:bg-[#111111] transition-colors" data-testid="services-rental-cta">
                {t("common.viewFleet")} <ArrowRight size={16} className="rtl:rotate-180" />
              </Link>
            </div>
            <div className="lg:col-span-5 order-1 lg:order-2">
              <div className="relative max-w-md mx-auto lg:max-w-none lg:ml-auto group">
                <div className="w-full aspect-[4/3] border-2 border-[#111111] group-hover:border-[#FF5A00] block-shadow-orange bg-gradient-to-br from-[#F8F8F8] to-[#EEEEEE] overflow-hidden flex items-center justify-center transition-all duration-300">
                  <img src={featuredImage} alt="RTC excavator" className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="absolute -top-4 -left-4 bg-[#FF5A00] text-[#111111] px-3 py-1.5 font-mono-rtc text-xs uppercase tracking-widest font-bold group-hover:bg-[#111111] group-hover:text-[#FF5A00] transition-colors duration-300">01</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14 bg-[#FAFAFA] grid-bg">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeader eyebrow={t("services.division2Label")} title={t("services.division2Title")} subtitle={t("services.division2Subtitle")} />
          <div className="mt-6 md:mt-8">
            <div className="mb-4 flex items-center gap-3">
              <span className="font-display uppercase font-bold text-lg md:text-xl text-[#111111]">Other Services</span>
              <span className="w-8 h-[2px] bg-[#FF5A00]" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {other.map((s, i) => (
              <Link to={`/services/${s.slug}`} key={s.slug} className="group bg-white border-2 border-[#111111] hover:border-[#FF5A00] block-shadow-to-orange overflow-hidden flex flex-col transition-colors duration-300" data-testid={`service-card-${s.slug}`}>
                <div className="aspect-[16/10] overflow-hidden bg-[#F3F4F6]">
                  <img src={s.image} alt={pick(s.name)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-4 md:p-5 flex-1 flex flex-col">
                  <span className="font-mono-rtc text-[10px] uppercase tracking-[0.3em] text-[#52525B]">0{i + 1}</span>
                  <h3 className="font-display uppercase font-bold text-xl md:text-2xl mt-1.5 group-hover:text-[#FF5A00] transition-colors duration-300">{pick(s.name)}</h3>
                  <p className="mt-1.5 text-xs md:text-sm text-[#52525B]">{pick(s.short)}</p>
                  <p className="mt-2 text-[#111111]/80 leading-relaxed text-sm line-clamp-3">{pick(s.description)}</p>
                  <div className="mt-4 inline-flex items-center gap-2 font-bold uppercase tracking-wide text-xs md:text-sm text-[#111111] group-hover:text-[#FF5A00] transition-colors duration-300">
                    {t("common.learnMore")} <ArrowRight size={14} className="rtl:rotate-180 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14 bg-[#111111] text-white">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h3 className="font-display uppercase font-bold text-2xl sm:text-3xl md:text-4xl">{t("services.ctaTitle")}</h3>
          <p className="mt-5 text-white/70 max-w-xl mx-auto">{t("services.ctaSubtitle")}</p>
          <Link to="/contact" className="mt-8 inline-flex items-center gap-2 bg-[#FF5A00] text-white px-8 py-4 font-bold uppercase tracking-wide hover:bg-white hover:text-[#111111] transition-colors" data-testid="services-contact-cta">
            {t("common.requestQuote")} <ArrowRight size={16} className="rtl:rotate-180" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
